import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  ChevronDown,
  Hammer,
  Compass,
  Landmark,
  Eye,
} from "lucide-react";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import LandingFooter from "../components/LandingFooter";

/**
 * Public landing page at "/".
 *
 * Editorial civic-tech composition — Fraunces headlines, Newsreader italic
 * accents, Nunito Sans body + tabular numerals, no gradients, no glassmorphism.
 *
 * Micro-interactions: staggered reveal on scroll, count-up numbers, animated
 * eyebrow rules, hover-shift CTAs, scroll cue, and a slim terracotta page
 * progress bar. All respect prefers-reduced-motion.
 */
export default function Landing() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/marketplace/stats")
      .then((r) => alive && setStats(r.data))
      .catch(() => {
        /* spec defaults below */
      });
    return () => {
      alive = false;
    };
  }, []);

  const counts = {
    designs: stats?.approved_designs ?? 12,
    parcels: stats?.parcels_total ?? 134,
    firms: stats?.architects_verified ?? 5,
    neighborhoods: 4,
  };

  return (
    <div data-testid="landing-page">
      <ScrollProgress />
      <Hero />
      <CredibilityStrip counts={counts} />
      <ProblemStatement />
      <ThreePillars />
      <PickEntry />
      <HowItWorks />
      <ClosingCTA />
      <LandingFooter />
    </div>
  );
}

/* ─────────── Page progress bar (fixed, terracotta hairline) ─────────── */

function ScrollProgress() {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setPct(max > 0 ? Math.min(100, (h.scrollTop / max) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);
  return (
    <div
      aria-hidden
      data-testid="landing-scroll-progress"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        background: "transparent",
        zIndex: 60,
        pointerEvents: "none",
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: "100%",
          background: "#B14E2F",
          transition: "width 80ms linear",
        }}
      />
    </div>
  );
}

/* ─────────── Reveal wrapper (IntersectionObserver) ─────────── */

function useInView({ threshold = 0.18, once = true, rootMargin = "0px 0px -8% 0px" } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            if (once) io.unobserve(e.target);
          } else if (!once) {
            setInView(false);
          }
        });
      },
      { threshold, rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, once, rootMargin]);
  return [ref, inView];
}

function Reveal({ children, delay = 0, as: As = "div", style, ...rest }) {
  const [ref, inView] = useInView();
  return (
    <As
      ref={ref}
      className={`wn-reveal${inView ? " is-visible" : ""}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}
      {...rest}
    >
      {children}
    </As>
  );
}

/* ─────────── CountUp number (Nunito Sans tabular) ─────────── */

function CountUp({
  to,
  prefix = "",
  suffix = "",
  duration = 1100,
  separator = true,
  className,
  style,
  ...rest
}) {
  const [ref, inView] = useInView({ threshold: 0, rootMargin: "0px 0px -10% 0px" });
  const [v, setV] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!inView || startedRef.current) return;
    startedRef.current = true;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setV(to);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out-cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.round(to * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  const text = separator ? v.toLocaleString("en-US") : String(v);
  return (
    <span
      ref={ref}
      className={["tabular-nums", className].filter(Boolean).join(" ")}
      style={style}
      {...rest}
    >
      {prefix}
      {text}
      {suffix}
    </span>
  );
}

/* ───────────────────────── HERO ───────────────────────── */

function Hero() {
  const navigate = useNavigate();
  const { signOut } = useRole();

  // Clicking "Browse the marketplace" from the Landing should always land
  // the visitor on the *public* marketplace, regardless of whether a stale
  // session sits in localStorage from a previous visit. Without this guard,
  // the persisted role drove the Shell to render portal chrome (LeftRail,
  // role-specific menus) around the marketplace — making it look like the
  // visitor was signed in when they weren't actively signed in.
  const onBrowseMarketplace = (e) => {
    e.preventDefault();
    signOut();
    navigate("/marketplace");
  };

  return (
    <section
      className="grid lg:grid-cols-[1.35fr_1fr] gap-14 items-end"
      style={{ paddingTop: 32, paddingBottom: 64, position: "relative" }}
      data-testid="landing-hero"
    >
      <div className="max-w-[760px]">
        <Reveal delay={0}>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="wn-rule-grow"
              style={{ "--rule-w": "32px" }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              City of Indianapolis · WHOLE Neighborhoods
            </span>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <h1
            className="mt-6"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: "clamp(44px, 6vw, 78px)",
              lineHeight: 1.02,
              letterSpacing: "-0.01em",
              color: "#1B1F4B",
            }}
            data-testid="landing-hero-title"
          >
            A digital marketplace for{" "}
            <span
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "#8F3C23",
              }}
            >
              pre-reviewed
            </span>{" "}
            infill housing.
          </h1>
        </Reveal>

        <Reveal delay={220}>
          <p
            className="mt-6 max-w-[620px]"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 21,
              lineHeight: 1.55,
            }}
          >
            Connecting homeowners, CDCs, licensed architects, and city staff to
            activate vacant Indianapolis parcels — and rebuild neighborhoods,
            one block at a time.
          </p>
        </Reveal>

        <Reveal delay={320}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/sign-in"
              className="wn-btn wn-btn-accent wn-cta-arrow"
              style={{ padding: "12px 22px", fontSize: 14 }}
              data-testid="landing-hero-primary-cta"
            >
              Enter the platform <ArrowRight size={16} />
            </Link>
            <Link
              to="/marketplace"
              onClick={onBrowseMarketplace}
              className="wn-btn wn-btn-outline wn-cta-arrow"
              style={{ padding: "12px 22px", fontSize: 14 }}
              data-testid="landing-hero-secondary-cta"
            >
              Browse the marketplace <ChevronRight size={16} />
            </Link>
          </div>
        </Reveal>
      </div>

      {/* Right column — editorial dateline + supporting note */}
      <Reveal
        as="aside"
        delay={380}
        className="hidden lg:block"
        style={{
          borderLeft: "1px solid #D9D8E4",
          paddingLeft: 32,
          paddingBottom: 8,
        }}
        aria-hidden
      >
        <div className="wn-label">Volume 01 · 2026</div>
        <div
          className="mt-2 tabular-nums"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontSize: 17,
            color: "#1B1F4B",
            lineHeight: 1.35,
          }}
        >
          A field study in
          <br />
          civic infill, written
          <br />
          parcel by parcel.
        </div>
        <p
          className="mt-5"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13.5,
            lineHeight: 1.55,
            maxWidth: 260,
          }}
        >
          A proof of concept built with the City of Indianapolis Department of
          Metropolitan Development and LISC Indianapolis.
        </p>
      </Reveal>

      {/* Scroll cue — subtle bounce, fades in late */}
      <Reveal
        delay={680}
        style={{ gridColumn: "1 / -1", justifySelf: "center", marginTop: 24 }}
        aria-hidden
      >
        <div
          className="wn-scroll-cue inline-flex items-center gap-2"
          style={{ color: "#6C6B85", fontSize: 11, letterSpacing: "0.18em" }}
          data-testid="landing-scroll-cue"
        >
          <span className="wn-label">Scroll</span>
          <ChevronDown size={14} />
        </div>
      </Reveal>
    </section>
  );
}

/* ─────────────────── CREDIBILITY STRIP ─────────────────── */

function CredibilityStrip({ counts }) {
  const cells = [
    { v: counts.designs, label: "Pre-reviewed designs" },
    { v: counts.parcels, label: "Vetted parcels" },
    { v: counts.firms, label: "Verified firms" },
    { v: counts.neighborhoods, label: "Active neighborhoods" },
  ];
  return (
    <section
      style={{
        borderTop: "1px solid #D9D8E4",
        borderBottom: "1px solid #D9D8E4",
        padding: "28px 0",
      }}
      data-testid="landing-credibility-strip"
    >
      <div className="grid md:grid-cols-[1fr_auto] items-center gap-8">
        <Reveal>
          <div
            className="wn-label"
            style={{ color: "#6C6B85", letterSpacing: "0.14em" }}
          >
            Proof of concept · in partnership with
            <span style={{ color: "#1B1F4B" }}> City of Indianapolis DMD</span>{" "}
            &amp; <span style={{ color: "#1B1F4B" }}>LISC Indianapolis</span>
          </div>
        </Reveal>
        <div className="flex items-stretch flex-wrap" data-testid="landing-counts">
          {cells.map((c, i) => (
            <Reveal
              key={c.label}
              delay={i * 90}
              style={{
                padding: i === 0 ? "0 28px 0 0" : "0 28px",
                borderLeft: i === 0 ? "none" : "1px solid #D9D8E4",
              }}
            >
              <CountUp
                to={c.v}
                style={{
                  fontWeight: 700,
                  fontSize: 28,
                  lineHeight: 1,
                  color: "#1B1F4B",
                  display: "block",
                }}
                data-testid={`landing-count-${c.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              />
              <div className="wn-label mt-2">{c.label}</div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────── PROBLEM STATEMENT ────────────── */

function ProblemStatement() {
  return (
    <section
      className="grid lg:grid-cols-[1.1fr_1fr] gap-14"
      style={{ paddingTop: 112, paddingBottom: 112 }}
      data-testid="landing-problem"
    >
      <Reveal>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="wn-rule-grow"
            style={{ "--rule-w": "28px" }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            § 01 · The problem
          </span>
        </div>
        <h2
          className="mt-6"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: "clamp(40px, 5vw, 64px)",
            lineHeight: 1.05,
            color: "#8F3C23",
            letterSpacing: "-0.005em",
          }}
        >
          The cost of{" "}
          <span style={{ fontStyle: "italic" }}>doing nothing.</span>
        </h2>
      </Reveal>

      <Reveal
        delay={140}
        style={{ borderLeft: "1px solid #D9D8E4", paddingLeft: 32 }}
      >
        <p
          style={{
            color: "#272636",
            fontSize: 17,
            lineHeight: 1.65,
            maxWidth: 520,
          }}
        >
          Across{" "}
          <span
            className="tabular-nums"
            style={{ fontWeight: 700, color: "#1B1F4B" }}
          >
            134
          </span>{" "}
          surveyed Indianapolis parcels, vacant lots generate an aggregate
          drain of{" "}
          <span className="wn-accent-underline">
            <CountUp
              to={117860}
              prefix="$"
              suffix=" per year"
              duration={1400}
              style={{ fontWeight: 700, color: "#8F3C23" }}
              data-testid="landing-vacancy-drain"
            />
          </span>{" "}
          in upkeep, nuisance abatement, and liability — before a single home
          is built.
        </p>
        <p
          className="mt-5"
          style={{
            color: "#272636",
            fontSize: 17,
            lineHeight: 1.65,
            maxWidth: 520,
          }}
        >
          WHOLE Neighborhoods compresses the pipeline from vacant lot to
          occupied home: pre-reviewed plans, verified architects, and a
          permitting path designed for speed without losing oversight.
        </p>
        <div
          className="mt-7 grid grid-cols-3 gap-5"
          style={{ borderTop: "1px solid #D9D8E4", paddingTop: 20 }}
          data-testid="landing-problem-stats"
        >
          <Mini value="$117,860" label="Annual vacancy drain" emphasis />
          <Mini value="10-day" label="Pre-reviewed permit path" />
          <Mini value="4" label="Target neighborhoods" />
        </div>
      </Reveal>
    </section>
  );
}

function Mini({ value, label, emphasis }) {
  return (
    <div>
      <div
        className="tabular-nums"
        style={{
          fontWeight: 700,
          fontSize: 22,
          color: emphasis ? "#8F3C23" : "#1B1F4B",
          lineHeight: 1.05,
        }}
      >
        {value}
      </div>
      <div className="wn-label mt-2">{label}</div>
    </div>
  );
}

/* ─────────────── LISC THREE PILLARS ─────────────── */

const PILLARS = [
  {
    title: "Quality Housing & Community Spaces",
    body:
      "A pre-reviewed catalog of designs and a transparent database of vetted parcels accelerate code-compliant infill across target neighborhoods.",
  },
  {
    title: "Economic Opportunity",
    body:
      "Direct commissions to verified MBE / WBE / VBE Indianapolis firms keep design fees circulating in the city — and surface local talent to owners.",
  },
  {
    title: "Healthy & Safe Communities",
    body:
      "Blighted vacant parcels are replaced with occupied homes. A diversity heatmap prevents monoculture and protects neighborhood character.",
  },
];

function ThreePillars() {
  return (
    <section
      style={{
        borderTop: "1px solid #D9D8E4",
        paddingTop: 96,
        paddingBottom: 112,
      }}
      data-testid="landing-pillars"
    >
      <Reveal>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="wn-rule-grow"
            style={{ "--rule-w": "28px" }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            § 02 · Strategic alignment
          </span>
        </div>
      </Reveal>
      <div className="grid lg:grid-cols-[1.05fr_1.4fr] gap-12 items-end mt-5">
        <Reveal delay={80}>
          <h2
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: "clamp(36px, 4.6vw, 56px)",
              lineHeight: 1.05,
              color: "#1B1F4B",
              letterSpacing: "-0.005em",
              margin: 0,
            }}
          >
            Built for the{" "}
            <span style={{ fontStyle: "italic", color: "#8F3C23" }}>
              three pillars
            </span>
            .
          </h2>
        </Reveal>
        <Reveal delay={180}>
          <p
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 18,
              lineHeight: 1.55,
              maxWidth: 560,
            }}
          >
            Every workflow on the platform maps directly to LISC
            Indianapolis&rsquo;s published strategic priorities — not as
            marketing language, but as measurable surface.
          </p>
        </Reveal>
      </div>

      <div
        className="mt-12 grid lg:grid-cols-3 gap-10"
        data-testid="landing-pillar-grid"
      >
        {PILLARS.map((p, i) => (
          <Reveal
            key={p.title}
            delay={i * 110}
            className="wn-pillar"
            style={{ borderLeft: "2px solid #B14E2F", paddingLeft: 20 }}
            data-testid={`landing-pillar-${i + 1}`}
          >
            <div
              className="wn-label tabular-nums"
              style={{ color: "#8F3C23" }}
            >
              Pillar {String(i + 1).padStart(2, "0")}
            </div>
            <h3
              className="mt-2"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 22,
                color: "#1B1F4B",
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              {p.title}
            </h3>
            <p
              className="mt-3"
              style={{
                color: "#272636",
                fontSize: 14.5,
                lineHeight: 1.65,
              }}
            >
              {p.body}
            </p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ─────────────── PERSONA ENTRY POINTS ─────────────── */

const PERSONAS = [
  {
    icon: Hammer,
    role: "Homeowner · CDC · Builder",
    desc:
      "Find a pre-reviewed design that fits your parcel, save what you love, request a consultation, and track every milestone from intake to permit.",
    cta: "Sign in as owner",
    to: "/sign-in",
    testid: "persona-owner",
  },
  {
    icon: Compass,
    role: "Licensed Architect",
    desc:
      "Publish pre-reviewed designs, take direct commissions from owners and CDCs, and manage consultations and construction administration from a single workspace.",
    cta: "Apply to the network",
    to: "/register/architect",
    testid: "persona-architect",
  },
  {
    icon: Landmark,
    role: "City Staff · DMD",
    desc:
      "See housing production, vacancy drain, permitting velocity, and code-change tracking — alongside a diversity heatmap of the verified professional network.",
    cta: "Sign in as staff",
    to: "/sign-in",
    testid: "persona-city",
  },
  {
    icon: Eye,
    role: "Browse as guest",
    desc:
      "Read-only access to the public marketplace, the parcel database, the verified architect directory, and civic resources — no account required.",
    cta: "Open the catalog",
    to: "/marketplace",
    testid: "persona-guest",
  },
];

function PickEntry() {
  return (
    <section
      style={{
        borderTop: "1px solid #D9D8E4",
        paddingTop: 96,
        paddingBottom: 112,
      }}
      data-testid="landing-personas"
    >
      <div className="grid lg:grid-cols-[1fr_1.6fr] gap-12 items-end">
        <Reveal>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="wn-rule-grow"
              style={{ "--rule-w": "28px" }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              § 03 · Four ways in
            </span>
          </div>
          <h2
            className="mt-5"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: "clamp(36px, 4.6vw, 56px)",
              lineHeight: 1.05,
              color: "#1B1F4B",
              letterSpacing: "-0.005em",
              margin: 0,
            }}
          >
            Pick your{" "}
            <span style={{ fontStyle: "italic", color: "#8F3C23" }}>
              entry point
            </span>
            .
          </h2>
        </Reveal>
        <Reveal delay={140}>
          <p
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 17,
              lineHeight: 1.55,
              maxWidth: 560,
            }}
          >
            The platform serves four distinct workflows under one civic roof.
            Each entry point opens directly into the surface that&rsquo;s
            relevant to you.
          </p>
        </Reveal>
      </div>

      <div className="mt-12 grid md:grid-cols-2 gap-x-12 gap-y-2">
        {PERSONAS.map((p, i) => {
          const Icon = p.icon;
          return (
            <Reveal key={p.role} delay={i * 90}>
              <Link
                to={p.to}
                data-testid={p.testid}
                className="wn-persona-row group"
                style={{
                  display: "grid",
                  gridTemplateColumns: "44px 1fr auto",
                  alignItems: "start",
                  columnGap: 18,
                  padding: "26px 4px",
                  borderTop: "1px solid #D9D8E4",
                  borderBottom:
                    i >= PERSONAS.length - 2 ? "1px solid #D9D8E4" : "none",
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <span
                  className="wn-persona-icon"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 4,
                    border: "1px solid #D9D8E4",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#1B1F4B",
                    flexShrink: 0,
                  }}
                >
                  <Icon size={18} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div
                    className="wn-label tabular-nums"
                    style={{ color: "#8F3C23" }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3
                    className="mt-1"
                    style={{
                      fontFamily: '"Fraunces", Georgia, serif',
                      fontWeight: 600,
                      fontSize: 22,
                      color: "#1B1F4B",
                      lineHeight: 1.2,
                      margin: 0,
                    }}
                  >
                    {p.role}
                  </h3>
                  <p
                    className="mt-2"
                    style={{
                      color: "#272636",
                      fontSize: 14.5,
                      lineHeight: 1.6,
                      maxWidth: 480,
                    }}
                  >
                    {p.desc}
                  </p>
                  <span
                    className="mt-3 inline-flex items-center gap-1"
                    style={{
                      fontWeight: 700,
                      color: "#8F3C23",
                      fontSize: 13,
                    }}
                  >
                    <span className="wn-persona-cta">{p.cta}</span>
                    <ArrowUpRight size={14} />
                  </span>
                </div>
                <span
                  aria-hidden
                  className="wn-persona-arrow"
                  style={{
                    alignSelf: "center",
                    color: "#9AA0C0",
                    fontSize: 18,
                    fontFamily: '"Fraunces", Georgia, serif',
                  }}
                >
                  →
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

/* ───────────────── HOW IT WORKS ───────────────── */

const STEPS = [
  {
    n: "01",
    title: "Pick a parcel",
    body:
      "Browse 134 vetted Indianapolis infill lots — each surveyed for size, zoning, utilities, and carry costs.",
  },
  {
    n: "02",
    title: "Pick a design",
    body:
      "Choose from a catalog of architect-designed homes already pre-reviewed against city code and zoning.",
  },
  {
    n: "03",
    title: "Consult the architect",
    body:
      "Request a fit-and-feasibility consultation. Verified, licensed, insured. No inbox spam.",
  },
  {
    n: "04",
    title: "Permit & build",
    body:
      "Pre-reviewed status unlocks an accelerated city permit path. Track every milestone in one place.",
  },
];

function HowItWorks() {
  return (
    <section
      style={{
        borderTop: "1px solid #D9D8E4",
        paddingTop: 96,
        paddingBottom: 112,
      }}
      data-testid="landing-how-it-works"
    >
      <Reveal>
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="wn-rule-grow"
            style={{ "--rule-w": "28px" }}
          />
          <span className="wn-label" style={{ color: "#8F3C23" }}>
            § 04 · How it works
          </span>
        </div>
      </Reveal>
      <Reveal delay={100}>
        <h2
          className="mt-5 max-w-[820px]"
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: "clamp(36px, 4.6vw, 56px)",
            lineHeight: 1.1,
            color: "#1B1F4B",
            letterSpacing: "-0.005em",
            margin: 0,
            paddingBottom: 8,
          }}
        >
          From vacant lot to occupied home —{" "}
          <span style={{ fontStyle: "italic", color: "#8F3C23" }}>
            four steps
          </span>
          .
        </h2>
      </Reveal>

      <ol
        className="grid md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12"
        style={{ listStyle: "none", padding: 0, margin: 0, marginTop: 72 }}
        data-testid="landing-step-list"
      >
        {STEPS.map((s, i) => (
          <Reveal
            key={s.n}
            as="li"
            delay={i * 120}
            className="wn-step"
            data-testid={`landing-step-${s.n}`}
            tabIndex={0}
            style={{ outline: "none" }}
          >
            <div
              className="tabular-nums wn-step-num"
              style={{
                fontWeight: 700,
                fontSize: 52,
                lineHeight: 1,
                color: "#1B1F4B",
                letterSpacing: "-0.02em",
              }}
            >
              {s.n}
            </div>
            <span
              aria-hidden
              className="wn-step-rule block"
              style={{
                height: 2,
                background: "#B14E2F",
                marginTop: 16,
                marginBottom: 16,
                width: 40,
              }}
            />
            <h3
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 22,
                color: "#1B1F4B",
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              {s.title}
            </h3>
            <p
              className="mt-3"
              style={{ color: "#272636", fontSize: 14.5, lineHeight: 1.6 }}
            >
              {s.body}
            </p>
          </Reveal>
        ))}
      </ol>
    </section>
  );
}

/* ───────────────── CLOSING CTA (inverted indigo) ───────────────── */

function ClosingCTA() {
  return (
    <section
      data-testid="landing-closing-cta"
      style={{
        backgroundColor: "#1B1F4B",
        color: "#FBFAFD",
        marginTop: 32,
        marginLeft: "calc(50% - 50vw)",
        marginRight: "calc(50% - 50vw)",
        padding: "112px max(24px, calc(50vw - 760px))",
      }}
    >
      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-14 items-end">
        <div>
          <Reveal>
            <div
              className="wn-label"
              style={{
                color: "rgba(251,250,253,0.65)",
                letterSpacing: "0.14em",
              }}
            >
              § 05 · Begin
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h2
              className="mt-5"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 500,
                fontSize: "clamp(40px, 5.4vw, 68px)",
                lineHeight: 1.02,
                color: "#FBFAFD",
                letterSpacing: "-0.005em",
                margin: 0,
              }}
            >
              Rebuild the neighborhood.{" "}
              <span style={{ fontStyle: "italic", color: "#E89B7B" }}>
                One parcel at a time.
              </span>
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p
              className="mt-6 max-w-[520px]"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "rgba(251,250,253,0.7)",
                fontSize: 17,
                lineHeight: 1.6,
              }}
            >
              Indianapolis has the parcels, the architects, and the will.
              WHOLE Neighborhoods is the connective tissue.
            </p>
          </Reveal>
        </div>
        <Reveal
          delay={260}
          className="flex flex-wrap items-center gap-3 lg:justify-end"
        >
          <Link
            to="/sign-in"
            className="wn-btn wn-cta-arrow"
            data-testid="landing-closing-primary"
            style={{
              backgroundColor: "#B14E2F",
              color: "#FBFAFD",
              border: "1px solid #B14E2F",
              padding: "14px 24px",
              fontSize: 14,
            }}
          >
            Sign in to the platform <ArrowRight size={16} />
          </Link>
          <Link
            to="/register/architect"
            className="wn-btn wn-cta-arrow"
            data-testid="landing-closing-secondary"
            style={{
              backgroundColor: "transparent",
              color: "#FBFAFD",
              border: "1px solid rgba(251,250,253,0.4)",
              padding: "14px 24px",
              fontSize: 14,
            }}
          >
            Apply as an architect <ChevronRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
